import type { OrderBy } from '@/lib/graphql-query-builder';

/**
 * Scope note: covers listing survey batches, sending a new batch to
 * explicitly-picked recipients, viewing a batch's per-recipient submission
 * status, and viewing one recipient's completed response (read-only,
 * reusing the same question-rendering approach as hr-my-submissions). PDF
 * export is deferred, matching earlier CSV/PDF-export scope cuts.
 */
type SubmissionStatus = 'Pending' | 'Completed';

export interface SurveyBatch {
  ItemId: string;
  Title: string;
  Description?: string;
  SurveyFormId: string;
  /** Kind C: when the batch was sent (UTC), shown in `Timezone`. */
  SentDateUtc?: string;
  /** Kind A: deadline day, stored as `YYYY-MM-DDT00:00:00.000Z`. */
  Deadline?: string;
  /** IANA zone of the organization the batch was made in. */
  Timezone?: string;
  TotalRecipients: number;
  CompletedCount: number;
}

export interface SurveyBatchesPagingResult {
  hasNextPage: boolean;
  hasPreviousPage: boolean;
  totalCount: number;
  totalPages: number;
  pageSize: number;
  pageNo: number;
  items: SurveyBatch[];
}

export interface GetSurveyBatchesParams {
  pageNo: number;
  pageSize: number;
  filter?: Record<string, unknown>;
  /** Defaults to newest send first. */
  order?: OrderBy[];
}

export interface SurveySubmission {
  ItemId: string;
  SurveyBatchId: string;
  SurveyFormId?: string;
  SurveyTitle?: string;
  SurveyDescription?: string;
  UserId: string;
  UserName?: string;
  UserEmail?: string;
  /** Kind A: deadline day copied from the batch. */
  Deadline?: string;
  Status: SubmissionStatus;
  /** Kind C: when the survey was requested (UTC). */
  RequestedDateUtc?: string;
  /** Kind C: when the recipient submitted (UTC). */
  SubmittedDateUtc?: string;
  /** IANA zone of the organization the submission was made in; its kind C values are shown in it. */
  Timezone?: string;
  AnswersJson?: string;
}

export interface SurveySubmissionsPagingResult {
  hasNextPage: boolean;
  hasPreviousPage: boolean;
  totalCount: number;
  totalPages: number;
  pageSize: number;
  pageNo: number;
  items: SurveySubmission[];
}

export interface GetSurveySubmissionsParams {
  pageNo: number;
  pageSize: number;
  filter?: Record<string, unknown>;
  /** Defaults to recipients by name. */
  order?: OrderBy[];
}

export interface SurveyFormOption {
  ItemId: string;
  Title: string;
  HasSections: boolean;
  QuestionsJson: string;
  SectionsJson?: string;
}

interface SurveyFormSection {
  SectionId: string;
  Title: string;
  Description?: string;
  Position: number;
  Questions: Question[];
}

/**
 * Flattens a survey form's questions regardless of whether it is a flat
 * form (QuestionsJson) or a sectioned form (SectionsJson) -- sections are
 * ordered by Position, and questions within each section keep their own
 * Position. Section headings/descriptions are not rendered here, only the
 * flattened question list (matches the same scope cut made in
 * hr-survey-my-submissions).
 */
export const getFormQuestions = (
  form?: Pick<SurveyFormOption, 'HasSections' | 'QuestionsJson' | 'SectionsJson'>
): Question[] => {
  if (!form) return [];
  if (form.HasSections) {
    try {
      const sections = JSON.parse(form.SectionsJson ?? '[]') as SurveyFormSection[];
      return [...sections]
        .sort((a, b) => a.Position - b.Position)
        .flatMap((section) => section.Questions ?? []);
    } catch {
      return [];
    }
  }
  try {
    return JSON.parse(form.QuestionsJson) as Question[];
  } catch {
    return [];
  }
};

interface EmployeeOption {
  ItemId: string;
  FirstName: string;
  LastName: string;
  DisplayName?: string;
  Email: string;
}

export interface EmployeesLookupResult {
  totalCount: number;
  items: EmployeeOption[];
}

interface SurveyRecipient {
  UserId: string;
  UserName: string;
  UserEmail: string;
}

export interface SendSurveyInput {
  Title: string;
  Description?: string;
  SurveyFormId: string;
  Deadline?: string;
  Recipients: SurveyRecipient[];
}

export interface Question {
  QuestionId: string;
  FormatType: string;
  Question: string;
}

export interface AnswerValue {
  QuestionId: string;
  Answer?: string;
  Rating?: number;
  McOptionsValue?: string;
  CbOptionsValues?: string[];
  LikertStatementValues?: Record<string, string>;
}
